import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  makeStyles,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import NETWORK_CONSTANTS from '../../../constants/network.constants';
import STORAGE_CONSTANTS from '../../../constants/storage.constants';
import { lightGrey } from '../../../app/theme';

type AdminPermissionUser = {
  id: number;
  name: string;
  email: string;
  role: 'Admin' | 'Manager';
  isCurrentUser: boolean;
  protected: boolean;
  protectionReason: string;
};

type FormMode = 'add' | 'edit' | null;
type AuditFormat = 'csv' | 'zip';
type PermissionRole = 'Student' | 'Admin' | 'Manager';

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    margin: '17px 0 30px',
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
    color: theme.palette.secondary.main,
    fontSize: 32,
    fontWeight: 600,
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
    marginBottom: 18,
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },
  sectionHeader: {
    fontSize: 20,
    fontWeight: 600,
  },
  actionButton: {
    borderRadius: 21,
    minWidth: 128,
    minHeight: 42,
  },
  table: {
    minWidth: 650,
    '& .MuiTableCell-root': {
      fontSize: 16,
    },
    '& .MuiTableCell-head': {
      fontSize: 16,
    },
  },
  tableHead: {
    backgroundColor: theme.palette.grey[100],
  },
  columnHeader: {
    fontWeight: 600,
  },
  protectedText: {
    color: theme.palette.text.disabled,
    fontSize: 'inherit',
    fontWeight: 600,
  },
  warningMessage: {
    maxWidth: 1020,
    marginBottom: 24,
    color: theme.palette.error.main,
    fontSize: 14,
    fontWeight: 600,
  },
  editButton: {
    minWidth: 0,
    paddingLeft: 0,
    paddingRight: 0,
    fontSize: 'inherit',
    fontWeight: 600,
    textTransform: 'none',
  },
  formCard: {
    maxWidth: 720,
    marginTop: 28,
    padding: 24,
  },
  formTitle: {
    fontSize: 20,
    fontWeight: 600,
  },
  formDescription: {
    maxWidth: 640,
    margin: '8px 0 18px',
    fontSize: 14,
    lineHeight: 1.4,
  },
  formField: {
    width: '100%',
    maxWidth: 520,
  },
  inputLabel: {
    color: lightGrey,
    textTransform: 'uppercase',
  },
  confirmationText: {
    maxWidth: 640,
    marginBottom: 20,
    color: theme.palette.error.main,
    fontSize: 14,
    lineHeight: 1.45,
  },
  buttonRow: {
    display: 'flex',
    gap: 14,
    marginTop: 20,
  },
  errorMessage: {
    marginTop: 14,
    color: theme.palette.error.main,
    fontSize: 14,
  },
  successMessage: {
    marginTop: 18,
    color: theme.palette.success.main,
    fontSize: 14,
    fontWeight: 600,
  },
  emptyMessage: {
    padding: 28,
    textAlign: 'center',
  },
  auditSection: {
    maxWidth: 720,
    marginTop: 36,
  },
  auditControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    marginTop: 18,
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },
  formatField: {
    width: 180,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  instructions: {
    maxWidth: 900,
    padding: '0 0 65px',
    marginTop: 36,
  },
  paragraph: {
    paddingTop: 10,
    fontSize: 14,
    lineHeight: 1.3,
  },
}));

const getErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  const responseData: { error?: string } = await response.json().catch(() => ({}));
  return responseData.error || fallback;
};

const AdminPermissions = (): JSX.Element => {
  const classes = useStyles();
  const [users, setUsers] = useState<AdminPermissionUser[]>([]);
  const [managerCount, setManagerCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [selectedUser, setSelectedUser] = useState<AdminPermissionUser | null>(null);
  const [email, setEmail] = useState('');
  const [targetRole, setTargetRole] = useState<PermissionRole>('Admin');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [auditFormat, setAuditFormat] = useState<AuditFormat>('csv');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const authenticatedFetch = useCallback((url: string, options?: RequestInit) => {
    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `JWT ${localStorage.getItem(STORAGE_CONSTANTS.TOKEN_KEY)}`,
        ...(options?.headers || {}),
      },
    });
  }, []);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const response = await authenticatedFetch(
        `${NETWORK_CONSTANTS.BASE_URL}${NETWORK_CONSTANTS.API_ADMIN_PERMISSIONS}`,
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Admin permissions could not be loaded.'));
      }
      const data: { results: AdminPermissionUser[]; managerCount: number } = await response.json();
      setUsers(data.results);
      setManagerCount(data.managerCount);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Admin permissions could not be loaded.');
    } finally {
      setIsLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!successMessage) return undefined;

    const clearSuccessMessage = window.setTimeout(() => {
      setSuccessMessage('');
    }, 6000);

    return () => window.clearTimeout(clearSuccessMessage);
  }, [successMessage]);

  const closeForm = () => {
    setFormMode(null);
    setSelectedUser(null);
    setEmail('');
    setTargetRole('Admin');
    setIsConfirming(false);
    setFormError('');
  };

  const openAddForm = () => {
    closeForm();
    setSuccessMessage('');
    setFormMode('add');
  };

  const openEditForm = (user: AdminPermissionUser) => {
    closeForm();
    setSuccessMessage('');
    setSelectedUser(user);
    setEmail(user.email);
    setTargetRole(user.role === 'Manager' ? 'Admin' : 'Student');
    setFormMode('edit');
  };

  const prepareConfirmation = () => {
    setFormError('');
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
      setFormError('Enter a valid email address.');
      return;
    }

    if (formMode === 'add') {
      const existingUser = users.find((user) => user.email.toLowerCase() === normalizedEmail);
      if (existingUser) {
        setFormError(
          existingUser.role === 'Admin'
            ? 'The person with this email is already an Admin.'
            : 'The person with this email is already a Manager.',
        );
        return;
      }
    }

    setEmail(normalizedEmail);
    setIsConfirming(true);
  };

  const cancelOrReturnToForm = () => {
    if (isConfirming) {
      setIsConfirming(false);
      setFormError('');
      return;
    }

    closeForm();
  };

  const submitRoleChange = async () => {
    if (!formMode) return;

    setIsSubmitting(true);
    setFormError('');
    const newRole = formMode === 'add' ? 'Admin' : targetRole;
    try {
      const response = await authenticatedFetch(
        `${NETWORK_CONSTANTS.BASE_URL}${NETWORK_CONSTANTS.API_ADMIN_PERMISSIONS}`,
        {
          method: 'POST',
          body: JSON.stringify({ email, role: newRole }),
        },
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'The user’s role could not be changed.'));
      }
      const data: { message: string } = await response.json();
      closeForm();
      setSuccessMessage(`${data.message} Once this message disappears, you can make another Admin permission change.`);
      await loadUsers();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'The user’s role could not be changed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadAuditLog = async () => {
    setIsDownloading(true);
    setDownloadError('');
    try {
      const response = await authenticatedFetch(
        `${NETWORK_CONSTANTS.BASE_URL}${NETWORK_CONSTANTS.API_ADMIN_PERMISSIONS_AUDIT}`,
        {
          method: 'POST',
          body: JSON.stringify({ format: auditFormat }),
        },
      );
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'The audit log could not be generated.'));
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/);
      link.href = objectUrl;
      link.download = filenameMatch ? filenameMatch[1] : `Blue_Earth_Admin_Permissions_Audit.${auditFormat}`;
      link.setAttribute('style', 'visibility:hidden');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : 'The audit log could not be downloaded.');
    } finally {
      setIsDownloading(false);
    }
  };

  const currentRole = formMode === 'add' ? 'Student' : selectedUser?.role;
  const isPromotion =
    (currentRole === 'Student' && targetRole === 'Admin') || (currentRole === 'Admin' && targetRole === 'Manager');
  const roleChangeEffect = (() => {
    if (currentRole === 'Student' && targetRole === 'Admin') {
      return 'They will gain access to the Admin Portal.';
    }
    if (currentRole === 'Admin' && targetRole === 'Manager') {
      return 'They will be able to manage other users’ Admin permissions.';
    }
    if (currentRole === 'Manager' && targetRole === 'Admin') {
      return 'They will keep Admin Portal access but will no longer manage permissions.';
    }
    return 'They will immediately lose access to the Admin Portal.';
  })();
  const confirmationText = `Are you sure you want to ${isPromotion ? 'promote' : 'demote'} ${
    selectedUser?.name || email
  } from ${currentRole} to ${targetRole}? ${roleChangeEffect}`;
  let submitButtonText = 'Submit';
  if (isSubmitting) {
    submitButtonText = 'Saving…';
  } else if (isConfirming) {
    submitButtonText = 'Confirm Submit';
  }

  const renderUserAction = (user: AdminPermissionUser): JSX.Element => {
    if (user.isCurrentUser) {
      return (
        <Typography className={classes.protectedText} title="You cannot change your own permissions.">
          Current User
        </Typography>
      );
    }
    if (user.protected) {
      return (
        <Typography className={classes.protectedText} title={user.protectionReason}>
          Protected
        </Typography>
      );
    }
    return (
      <Button className={classes.editButton} color="secondary" onClick={() => openEditForm(user)}>
        Edit User
      </Button>
    );
  };

  return (
    <section aria-labelledby="admin-permissions-title">
      <Typography id="admin-permissions-title" className={classes.title}>
        Admin Permissions
      </Typography>

      <div className={classes.tableHeader}>
        <Typography className={classes.sectionHeader}>Admins and Managers</Typography>
        <Button className={classes.actionButton} variant="contained" color="secondary" onClick={openAddForm}>
          Add New Admin
        </Button>
      </div>

      {!isLoading && !loadError && managerCount === 1 && (
        <Typography className={classes.warningMessage} role="status">
          Only one person currently has the Manager role. Consider promoting another Admin to Manager so more
          than one person can manage Admin permissions.
        </Typography>
      )}

      {loadError && <Typography className={classes.errorMessage}>{loadError}</Typography>}
      <TableContainer component={Paper} variant="outlined">
        <Table className={classes.table} aria-label="Admin and Manager permissions">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.columnHeader}>Name</TableCell>
              <TableCell className={classes.columnHeader}>Email</TableCell>
              <TableCell className={classes.columnHeader}>Role</TableCell>
              <TableCell className={classes.columnHeader}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className={classes.emptyMessage}>
                  No Admin or Manager accounts were found.
                </TableCell>
              </TableRow>
            )}
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} className={classes.emptyMessage}>
                  Loading permissions…
                </TableCell>
              </TableRow>
            )}
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>{renderUserAction(user)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {formMode && (
        <Paper className={classes.formCard} variant="outlined">
          <Typography className={classes.formTitle}>{formMode === 'add' ? 'Add New Admin' : 'Edit User'}</Typography>
          <Typography className={classes.formDescription}>
            {formMode === 'add'
              ? 'Enter the email address of an existing Student to promote them to Admin. They will gain access to the Admin Portal.'
              : 'Choose one neighboring role. Role changes must follow Student, Admin, Manager one level at a time.'}
          </Typography>
          {isConfirming ? (
            <Typography className={classes.confirmationText}>{confirmationText}</Typography>
          ) : (
            <>
              <TextField
                className={classes.formField}
                variant="outlined"
                label="EMAIL ADDRESS"
                value={email}
                disabled={formMode === 'edit'}
                onChange={(event) => setEmail(event.target.value)}
                InputLabelProps={{ className: classes.inputLabel }}
              />
              {formMode === 'edit' && (
                <TextField
                  className={classes.formField}
                  select
                  variant="outlined"
                  label="NEW ROLE"
                  value={targetRole}
                  onChange={(event) => setTargetRole(event.target.value as PermissionRole)}
                  InputLabelProps={{ className: classes.inputLabel }}
                  margin="normal"
                >
                  {selectedUser?.role === 'Admin' && <MenuItem value="Student">Student</MenuItem>}
                  {selectedUser?.role === 'Admin' && <MenuItem value="Manager">Manager</MenuItem>}
                  {selectedUser?.role === 'Manager' && <MenuItem value="Admin">Admin</MenuItem>}
                </TextField>
              )}
            </>
          )}
          {formError && <Typography className={classes.errorMessage}>{formError}</Typography>}
          <div className={classes.buttonRow}>
            <Button
              className={classes.actionButton}
              variant="outlined"
              onClick={cancelOrReturnToForm}
              disabled={isSubmitting}
            >
              {isConfirming ? 'Back' : 'Cancel'}
            </Button>
            <Button
              className={classes.actionButton}
              variant="contained"
              color="secondary"
              onClick={isConfirming ? submitRoleChange : prepareConfirmation}
              disabled={isSubmitting}
            >
              {submitButtonText}
            </Button>
          </div>
        </Paper>
      )}

      {successMessage && (
        <Typography className={classes.successMessage} role="status">
          {successMessage}
        </Typography>
      )}

      <div className={classes.auditSection}>
        <Typography className={classes.sectionHeader}>Download Audit Log</Typography>
        <Typography className={classes.paragraph}>
          Download the complete history of Admin permission changes for this platform.
        </Typography>
        <div className={classes.auditControls}>
          <TextField
            className={classes.formatField}
            select
            variant="outlined"
            label="FILE FORMAT"
            value={auditFormat}
            onChange={(event) => setAuditFormat(event.target.value as AuditFormat)}
            InputLabelProps={{ className: classes.inputLabel }}
          >
            <MenuItem value="csv">CSV</MenuItem>
            <MenuItem value="zip">ZIP</MenuItem>
          </TextField>
          <Button
            className={classes.actionButton}
            variant="contained"
            color="secondary"
            onClick={downloadAuditLog}
            disabled={isDownloading}
          >
            {isDownloading ? 'Preparing…' : 'Download Audit Log'}
          </Button>
        </div>
        {downloadError && <Typography className={classes.errorMessage}>{downloadError}</Typography>}
      </div>

      <div className={classes.instructions}>
        <Typography className={classes.sectionHeader}>To Manage Admin Permissions:</Typography>
        <Typography className={classes.paragraph}>
          Use “ADD NEW ADMIN” to promote an existing Student to Admin. Use “Edit User” to move another Admin or Manager
          one role level at a time: Student, Admin, Manager. You cannot edit your own permissions. BrickSimple and
          developer accounts are labeled “Protected”. Every successful change is recorded in the audit log.
        </Typography>
      </div>
    </section>
  );
};

export default AdminPermissions;
